import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Clock, Eye, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { quotesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';

export function DashboardPage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadQuotes();
  }, []);

  const loadQuotes = async () => {
    try {
      const res = await quotesAPI.getAll();
      setQuotes(res.data || []);
    } catch (error) {
      console.error('Failed to load quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      new: { label: t('quote.status.new'), variant: 'secondary' },
      in_progress: { label: t('quote.status.in_progress'), variant: 'default' },
      sent: { label: t('quote.status.sent'), variant: 'outline' },
      won: { label: t('quote.status.won'), variant: 'default', className: 'bg-green-600' },
      lost: { label: t('quote.status.lost'), variant: 'destructive' },
    };
    const config = statusMap[status] || statusMap.new;
    return <Badge variant={config.variant} className={config.className}>{config.label}</Badge>;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-8 md:py-12" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-2 font-['Outfit']">
          {t('nav.dashboard')}
        </h1>
        <p className="text-muted-foreground">
          {language === 'fr' ? `Bienvenue, ${user?.name}` : `Welcome, ${user?.name}`}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        <div className="border border-border p-6">
          <FileText className="h-6 w-6 text-[#DC2626] mb-3" />
          <p className="text-2xl font-bold">{quotes.length}</p>
          <p className="text-sm text-muted-foreground">
            {language === 'fr' ? 'Demandes de devis' : 'Quote requests'}
          </p>
        </div>
        <div className="border border-border p-6">
          <Clock className="h-6 w-6 text-blue-500 mb-3" />
          <p className="text-2xl font-bold">
            {quotes.filter(q => q.status === 'new' || q.status === 'in_progress').length}
          </p>
          <p className="text-sm text-muted-foreground">
            {language === 'fr' ? 'En attente' : 'Pending'}
          </p>
        </div>
        <div className="border border-border p-6">
          <Eye className="h-6 w-6 text-green-500 mb-3" />
          <p className="text-2xl font-bold">
            {quotes.filter(q => q.status === 'sent').length}
          </p>
          <p className="text-sm text-muted-foreground">
            {language === 'fr' ? 'Devis reçus' : 'Quotes received'}
          </p>
        </div>
        <div className="border border-border p-6">
          <ArrowRight className="h-6 w-6 text-purple-500 mb-3" />
          <p className="text-2xl font-bold">
            {quotes.filter(q => q.status === 'won').length}
          </p>
          <p className="text-sm text-muted-foreground">
            {language === 'fr' ? 'Commandes' : 'Orders'}
          </p>
        </div>
      </div>

      {/* Quotes List */}
      <div className="border border-border">
        <div className="p-4 border-b border-border bg-secondary flex items-center justify-between">
          <h2 className="font-semibold">{t('quote.myQuotes')}</h2>
          <Link to="/devis">
            <Button variant="outline" size="sm" className="rounded-none">
              {language === 'fr' ? 'Nouvelle demande' : 'New request'}
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="p-4 space-y-4">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
        ) : quotes.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-muted-foreground mb-4">
              {language === 'fr' ? 'Aucune demande de devis' : 'No quote requests'}
            </p>
            <Link to="/catalogue">
              <Button variant="outline" className="rounded-none">
                {t('hero.cta.catalog')}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {quotes.map((quote) => (
              <div
                key={quote.quote_id}
                className="p-4 hover:bg-secondary/50 transition-colors"
                data-testid={`quote-${quote.quote_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono text-sm text-muted-foreground">
                        #{quote.quote_id.slice(-8).toUpperCase()}
                      </span>
                      {getStatusBadge(quote.status)}
                    </div>
                    <p className="font-medium mb-1">{quote.company}</p>
                    <p className="text-sm text-muted-foreground">
                      {quote.items?.length || 0} {language === 'fr' ? 'produit(s)' : 'product(s)'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">
                      {formatDate(quote.created_at)}
                    </p>
                  </div>
                </div>
                {quote.items?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {quote.items.slice(0, 3).map((item, index) => (
                      <span key={index} className="text-xs px-2 py-1 bg-secondary">
                        {item.product_name} x{item.quantity}
                      </span>
                    ))}
                    {quote.items.length > 3 && (
                      <span className="text-xs px-2 py-1 bg-secondary text-muted-foreground">
                        +{quote.items.length - 3}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default DashboardPage;
